import React, { useState, useEffect } from 'react';
import { LinearProgress } from '@mui/material';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { RatingDialog } from '../Ratings';
import { ratingService, CourseRating } from '../../services/ratingService';
import { SimilarCourses } from '../Recommendations';
import { ExportButton } from '../Export';
import { MaterialSymbol } from '../common/MaterialSymbol';
import { devLog } from '../../utils/devLog';
import { sortMyCourses } from './myCoursesSort';
import './my-courses-design.css';

interface Enrollment {
    id: number;
    course: any;
    status: string;
    progressPercentage: number;
    finalScore?: number;
    enrolledAt: string;
    startedAt?: string;
    completedAt?: string;
    totalTimeSpent?: number;
    metadata?: Record<string, any>;
}

const MyCourses: React.FC = () => {
    const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [myRatings, setMyRatings] = useState<Record<number, CourseRating>>({});
    const [assignmentData, setAssignmentData] = useState<Record<number, { notes?: string; dueDate?: string }>>({});
    const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
    const [selectedCourseForRating, setSelectedCourseForRating] = useState<{ id: number; title: string } | null>(null);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const translate = useTranslate();

    useEffect(() => {
        loadEnrollments();
    }, []);

    const loadEnrollments = async () => {
        setLoading(true);
        try {
            // Load enrollments
            const { data: enrollmentsData } = await dataProvider.getList('course_enrollments', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'enrolledAt', order: 'DESC' },
                filter: {} // Will be filtered by userId automatically via EventSubscriber
            });

            // Extract unique course IDs
            const courseIds = Array.from(new Set(enrollmentsData.map((e: any) => getCourseId(e.course))));

            // Load all course details
            const { data: coursesData } = await dataProvider.getMany('courses', { ids: courseIds });

            // Create a map of course ID to course data
            const coursesMap = new Map(coursesData.map((c: any) => [c.id, c]));

            // Merge course data into enrollments and filter out courses from inactive catalogs
            const enrichedEnrollments = enrollmentsData
                .map((enrollment: any) => ({
                    ...enrollment,
                    course: coursesMap.get(getCourseId(enrollment.course)) || {
                        id: getCourseId(enrollment.course),
                        title: translate('myCourses.courseNotFound'),
                        description: ''
                    }
                }))
                .filter((enrollment: any) => {
                    // Filter out enrollments where course or catalog is inactive
                    const course = enrollment.course;

                    // Check if course itself is active
                    if (course && course.isActive === false) {
                        return false;
                    }

                    // Check if catalog is active
                    if (course && course.catalog && typeof course.catalog === 'object') {
                        if (course.catalog.isActive === false) {
                            return false;
                        }
                    }

                    // If all checks pass, include the enrollment
                    return true;
                });

            setEnrollments(enrichedEnrollments as Enrollment[]);

            // Load assignment info (notes, due dates)
            try {
                const token = localStorage.getItem('token');
                let userId: number | null = null;
                if (token) {
                    try { userId = JSON.parse(atob(token.split('.')[1])).userId ?? null; } catch {}
                }
                if (userId) {
                    const assignRes = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/course_assignments?userId=${userId}&status[]=assigned&status[]=in_progress`,
                        { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/ld+json' } }
                    );
                    if (assignRes.ok) {
                        const assignData = await assignRes.json();
                        const members = assignData['hydra:member'] ?? assignData['member'] ?? [];
                        const map: Record<number, { notes?: string; dueDate?: string }> = {};
                        members.forEach((a: any) => {
                            const cId = typeof a.course === 'object' ? a.course?.id : typeof a.course === 'string' ? parseInt(a.course.split('/').pop() || '0') : 0;
                            if (cId) map[cId] = { notes: a.notes || undefined, dueDate: a.dueDate || undefined };
                        });
                        setAssignmentData(map);
                    }
                }
            } catch {}

            // Load ratings for completed courses
            const completedCourseIds = enrichedEnrollments
                .filter((e: any) => e.status === 'completed')
                .map((e: any) => getCourseId(e.course));

            if (completedCourseIds.length > 0) {
                loadMyRatings(completedCourseIds);
            }
        } catch (error) {
            console.error('Error loading enrollments:', error);
            notify('Fehler beim Laden der Kurse', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadMyRatings = async (courseIds: number[]) => {
        const ratings: Record<number, CourseRating> = {};
        for (const courseId of courseIds) {
            try {
                const rating = await ratingService.getMyRating(courseId);
                if (rating) {
                    ratings[courseId] = rating;
                }
            } catch (error) {
                // Ignore errors for individual ratings
            }
        }
        setMyRatings(ratings);
    };

    const handleOpenRatingDialog = (courseId: number, courseTitle: string) => {
        setSelectedCourseForRating({ id: courseId, title: courseTitle });
        setRatingDialogOpen(true);
    };

    const handleRatingSubmitted = () => {
        // Reload ratings after submission
        const completedCourseIds = enrollments
            .filter(e => e.status === 'completed')
            .map(e => getCourseId(e.course));
        loadMyRatings(completedCourseIds);
    };

    const getCourseId = (course: any): number => {
        // If course is already a number (most common case)
        if (typeof course === 'number') {
            return course;
        }

        // If course is an object with an id property
        if (course && typeof course === 'object' && course.id) {
            return typeof course.id === 'number' ? course.id : parseInt(course.id);
        }

        // If course is a string
        if (typeof course === 'string') {
            // First try to parse it as a plain number string
            const plainNumber = parseInt(course);
            if (!isNaN(plainNumber)) {
                return plainNumber;
            }

            // Otherwise try IRI format (e.g., "/api/courses/123")
            const match = course.match(/\/(\d+)$/);
            return match ? parseInt(match[1]) : 0;
        }

        console.error('Could not extract course ID from:', course);
        return 0;
    };

    const handleStartCourse = (enrollment: Enrollment) => {
        devLog('handleStartCourse - enrollment:', enrollment);
        devLog('handleStartCourse - enrollment.course:', enrollment.course);
        devLog('handleStartCourse - enrollment.course type:', typeof enrollment.course);
        const courseId = getCourseId(enrollment.course);
        devLog('handleStartCourse - extracted courseId:', courseId);
        navigate(`/course-player/${courseId}`);
    };

    const handleContinueCourse = (enrollment: Enrollment) => {
        devLog('handleContinueCourse - enrollment:', enrollment);
        devLog('handleContinueCourse - enrollment.course:', enrollment.course);
        devLog('handleContinueCourse - enrollment.course type:', typeof enrollment.course);
        const courseId = getCourseId(enrollment.course);
        devLog('handleContinueCourse - extracted courseId:', courseId);
        navigate(`/course-player/${courseId}`);
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed':
                return translate('myCourses.status.completed');
            case 'in_progress':
                return translate('myCourses.status.inProgress');
            case 'failed':
                return translate('myCourses.status.failed');
            case 'enrolled':
                return translate('myCourses.status.notStarted');
            case 'pending_approval':
                return translate('myCourses.status.pendingApproval');
            case 'rejected':
                return translate('myCourses.status.rejected');
            case 'suspended':
                return translate('myCourses.status.suspended');
            default:
                return status;
        }
    };

    /** Chip-Variante aus der Design-Vorlage: prog=gelb, done=grün, failed=rot, sonst neutral */
    const getStatusClass = (status: string) => {
        switch (status) {
            case 'completed':
                return 'done';
            case 'in_progress':
                return 'prog';
            case 'failed':
                return 'failed';
            default:
                return 'notstarted';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatTime = (minutes?: number) => {
        if (!minutes) return '0 Min.';
        if (minutes < 60) return `${minutes} Min.`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    const filterEnrollments = (status?: string) => {
        if (!status) return enrollments;
        return enrollments.filter(e => e.status === status);
    };

    const renderCourseCard = (enrollment: Enrollment) => {
        const courseId = getCourseId(enrollment.course);
        const progress = Math.min(Math.round(enrollment.progressPercentage ?? 0), 100);
        const description = enrollment.course?.description || '';
        const assignment = assignmentData[courseId];
        const myRating = myRatings[courseId];

        return (
            <article key={enrollment.id} className="ccard" data-status={enrollment.status}>
                <div className="ctop">
                    <span className={`cbadge ${getStatusClass(enrollment.status)}`}>
                        {enrollment.status === 'in_progress' && <MaterialSymbol name="hourglass_top" />}
                        {getStatusLabel(enrollment.status)}
                    </span>
                </div>

                <h3>{enrollment.course?.title || 'Kurs ohne Titel'}</h3>
                <p className="desc">
                    {description.substring(0, 120)}
                    {description.length > 120 && '…'}
                </p>

                {assignment && (
                    <div className="cnote">
                        {assignment.dueDate && (
                            <strong>Fällig bis: {new Date(assignment.dueDate).toLocaleDateString('de-DE')} </strong>
                        )}
                        {assignment.notes && <span>— {assignment.notes}</span>}
                    </div>
                )}

                {enrollment.course?.recurrenceWeeks && enrollment.metadata?.renewalDueAt && (
                    <div className="cnote warn">
                        <strong>Wiederholung:</strong> Gültig bis {new Date(enrollment.metadata.renewalDueAt).toLocaleDateString('de-DE')}
                        {' '}(alle {enrollment.course.recurrenceWeeks} Wochen)
                    </div>
                )}

                <div className="cprog-head">
                    <span>Fortschritt</span>
                    <span>{progress}%</span>
                </div>
                <div className="cbar"><i style={{ width: `${progress}%` }} /></div>

                <div className="cmeta">
                    {enrollment.finalScore !== undefined && (
                        <div>Punktzahl: {enrollment.finalScore}%</div>
                    )}
                    <div>{translate('myCourses.enrolled')}: {formatDate(enrollment.enrolledAt)}</div>
                    {enrollment.totalTimeSpent ? (
                        <div>{translate('myCourses.learningTime')}: {formatTime(enrollment.totalTimeSpent)}</div>
                    ) : null}
                    {enrollment.completedAt && (
                        <div>{translate('myCourses.completed')}: {formatDate(enrollment.completedAt)}</div>
                    )}
                </div>

                {enrollment.status === 'completed' && myRating && (
                    <div className="crating">
                        <span className="lbl">{translate('courseRating.yourRating')}:</span>
                        {[1, 2, 3, 4, 5].map(star => (
                            <MaterialSymbol
                                key={star}
                                name="star"
                                filled
                                className={star <= myRating.rating ? 'on' : undefined}
                            />
                        ))}
                    </div>
                )}

                <div className="cactions">
                    {enrollment.status === 'enrolled' && (
                        <button type="button" className="cbtn solid" onClick={() => handleStartCourse(enrollment)}>
                            {translate('myCourses.buttons.start')}
                        </button>
                    )}
                    {enrollment.status === 'in_progress' && (
                        <button type="button" className="cbtn solid" onClick={() => handleContinueCourse(enrollment)}>
                            {translate('myCourses.buttons.continue')}
                        </button>
                    )}
                    {enrollment.status === 'completed' && (
                        <>
                            <button type="button" className="cbtn outline" onClick={() => handleContinueCourse(enrollment)}>
                                {translate('myCourses.buttons.review')}
                            </button>
                            <button
                                type="button"
                                className={`cbtn ${myRating ? 'outline' : 'solid'}`}
                                onClick={() => handleOpenRatingDialog(courseId, enrollment.course?.title || '')}
                            >
                                <MaterialSymbol name="star" filled />
                                {myRating
                                    ? translate('courseRating.editButton')
                                    : translate('courseRating.rateButton')}
                            </button>
                        </>
                    )}
                    {enrollment.status === 'failed' && (
                        <button type="button" className="cbtn danger" onClick={() => handleContinueCourse(enrollment)}>
                            {translate('myCourses.buttons.retry')}
                        </button>
                    )}
                </div>
            </article>
        );
    };

    if (loading) {
        return (
            <div className="oa-my-courses">
                <p className="empty-note">{translate('myCourses.loading')}</p>
                <LinearProgress sx={{ mt: 2 }} />
            </div>
        );
    }

    const inProgressCount = filterEnrollments('in_progress').length;
    const completedCount = filterEnrollments('completed').length;
    const notStartedCount = filterEnrollments('enrolled').length;

    const tabs: { label: string; status?: string; emptyKey: string }[] = [
        { label: translate('myCourses.tabs.all', { count: enrollments.length }), status: undefined, emptyKey: 'myCourses.noEnrollments' },
        { label: translate('myCourses.tabs.inProgress', { count: inProgressCount }), status: 'in_progress', emptyKey: 'myCourses.noCoursesInProgress' },
        { label: translate('myCourses.tabs.notStarted', { count: notStartedCount }), status: 'enrolled', emptyKey: 'myCourses.allCoursesStarted' },
        { label: translate('myCourses.tabs.completed', { count: completedCount }), status: 'completed', emptyKey: 'myCourses.noCoursesCompleted' },
    ];
    const activeTab = tabs[tabValue] ?? tabs[0];
    const visibleEnrollments = sortMyCourses(filterEnrollments(activeTab.status), assignmentData);

    return (
        <div className="oa-my-courses">
            <div className="mc-head">
                <ExportButton />
            </div>

            <div className="ktabs" role="tablist">
                {tabs.map((tab, index) => (
                    <button
                        key={tab.emptyKey}
                        type="button"
                        role="tab"
                        aria-selected={tabValue === index}
                        className={`ktab${tabValue === index ? ' active' : ''}`}
                        onClick={() => setTabValue(index)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {visibleEnrollments.length === 0 ? (
                <p className="empty-note">{translate(activeTab.emptyKey)}</p>
            ) : (
                <section className="course-grid">
                    {visibleEnrollments.map(renderCourseCard)}
                </section>
            )}

            {selectedCourseForRating && (
                <RatingDialog
                    open={ratingDialogOpen}
                    courseId={selectedCourseForRating.id}
                    courseName={selectedCourseForRating.title}
                    existingRating={myRatings[selectedCourseForRating.id] || null}
                    onClose={() => {
                        setRatingDialogOpen(false);
                        setSelectedCourseForRating(null);
                    }}
                    onSubmit={handleRatingSubmitted}
                />
            )}

            {/* Show similar courses based on most recently active course */}
            {enrollments.length > 0 && (() => {
                // Find the most recently active course (in_progress first, then completed)
                const inProgressEnrollment = enrollments.find(e => e.status === 'in_progress');
                const completedEnrollment = enrollments.find(e => e.status === 'completed');
                const activeCourse = inProgressEnrollment?.course || completedEnrollment?.course;

                if (activeCourse && getCourseId(activeCourse)) {
                    return (
                        <SimilarCourses
                            courseId={getCourseId(activeCourse)}
                            limit={4}
                        />
                    );
                }
                return null;
            })()}
        </div>
    );
};

export default MyCourses;
